import FileDownloaderButton from 'components/FileDownloaderButton';
import IconButton from 'components/IconButton';
import { useAtomValue, useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { extractApiErrorMessage } from 'lib/helper';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React from 'react';
import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { companyIdAtom } from 'views/state';

import BPAAssigneeModal from './components/BPAFindingsDataGrid/BPAAssigneeModal';
import BPAWatcherModal from './components/BPAFindingsDataGrid/BPAWatcherModal';
import { markBPAFindingNotesAsRead } from './services/bpaFindingsService';
import {
  findingsDataQueryParamsAtom,
  findingsDataRefreshAtom,
  selectedRowIdsAtom,
} from './state';

/**
 * Appends all query params from `params` to `url` as a query string.
 * When rows are selected, also appends `finding_ids` so the backend
 * scopes the export to only those records.
 */
const buildExportUrl = (
  url: string,
  params: Record<string, unknown>,
  selectedIds: number[],
): string => {
  const newParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      newParams.set(key, String(value));
    }
  }

  if (selectedIds.length > 0) {
    newParams.set('finding_ids', selectedIds.join(','));
  }

  const queryString = newParams.toString();
  const separator = url.includes('?') ? '&' : '?';
  return queryString ? `${url}${separator}${queryString}` : url;
};

const BulkActions: React.FC = () => {
  const companyId = useAtomValue(companyIdAtom);
  const findingsDataQueryParams = useAtomValue(findingsDataQueryParamsAtom);
  const selectedRowIds = useAtomValue(selectedRowIdsAtom);
  const resetSelectedRows = useResetAtom(selectedRowIdsAtom);
  const refreshFindingsGridData = useSetAtom(findingsDataRefreshAtom);
  const count = selectedRowIds.length;
  const [isAssigneeModalOpen, setAssigneeModalOpen] = React.useState(false);
  const [isWatcherBulkModalOpen, setWatcherBulkModalOpen] =
    React.useState(false);
  const [
    isMarkAllAsReadConfirmationModalOpen,
    setIsMarkAllAsReadConfirmationModalOpen,
  ] = React.useState(false);
  const [isMarkingNotesRead, setIsMarkingNotesRead] = React.useState(false);

  const markAllAsRead = async () => {
    if (!companyId || companyId === -1) {
      showFailureToast('Please select a company before marking notes as read.');
      return;
    }

    if (selectedRowIds.length === 0) {
      showFailureToast('No BPA findings selected.');
      return;
    }

    setIsMarkingNotesRead(true);

    try {
      await markBPAFindingNotesAsRead({
        company_id: companyId,
        finding_ids: selectedRowIds,
      });
      showSuccessToast('Selected finding notes marked as read.');
      refreshFindingsGridData(new Date().toISOString());
    } catch (err) {
      showFailureToast(
        extractApiErrorMessage(err, 'Error marking notes as read.'),
      );
    } finally {
      setIsMarkingNotesRead(false);
      setIsMarkAllAsReadConfirmationModalOpen(false);
    }
  };

  const MarkAllAsReadConfirmationModal = () => (
    <Modal
      isOpen={isMarkAllAsReadConfirmationModalOpen}
      toggle={() => setIsMarkAllAsReadConfirmationModalOpen(false)}
    >
      <ModalHeader>Mark all notes as read</ModalHeader>
      <ModalBody>
        <p>
          Are you sure you want to mark all notes as read for the selected (
          {count} ) findings?
        </p>
      </ModalBody>
      <ModalFooter>
        <Button
          color="secondary"
          onClick={() => setIsMarkAllAsReadConfirmationModalOpen(false)}
          disabled={isMarkingNotesRead}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={() => void markAllAsRead()}
          disabled={isMarkingNotesRead}
        >
          {isMarkingNotesRead ? 'Marking...' : 'Mark as read'}
        </Button>
      </ModalFooter>
    </Modal>
  );

  return (
    <>
      {count > 0 && (
        <>
          <span className="mr-1" style={{ fontSize: '1.2em' }}>
            <Badge color="primary" pill className="mx-1">
              {count}
            </Badge>
            Selected
          </span>
          <IconButton
            id="bpa_findings_clear_selection"
            iconClass="fas fa-times"
            tooltip="Clear all selections"
            onClick={resetSelectedRows}
          />
          <span
            className="mx-2"
            style={{
              fontSize: '24px',
              color: 'gray',
              verticalAlign: 'middle',
            }}
          >
            &#124;
          </span>
        </>
      )}
      <IconButton
        id="bpa_findings_mark_notes_read"
        iconClass="tim-icons icon-chat-33"
        tooltip="Mark all notes as read for selected BPA findings."
        onClick={() => setIsMarkAllAsReadConfirmationModalOpen(true)}
        disabled={count === 0}
      />
      <IconButton
        id="bpa_findings_assign_to_user"
        iconClass="fa fa-user-plus"
        tooltip="Bulk assign/unassign selected BPA findings."
        onClick={() => setAssigneeModalOpen(true)}
        disabled={count === 0}
      />
      <IconButton
        id="bpa_findings_assign_watchers"
        iconClass="fa fa-users"
        tooltip="Bulk add/remove watchers to selected BPA findings."
        onClick={() => setWatcherBulkModalOpen(true)}
        disabled={count === 0}
      />
      <FileDownloaderButton
        buttonId="bpa_findings_export"
        iconClass="fa fa-file-export"
        tooltip={
          count > 0
            ? `Export ${count} selected finding${count === 1 ? '' : 's'}. Active filters will also be applied.`
            : 'Export BPA findings. All active filters will be applied.'
        }
        fileName={`${(
          localStorage.getItem('currentCompany') || 'company'
        ).toLocaleLowerCase()}_bpa_findings.csv`}
        method="GET"
        downloadUrl={
          companyId
            ? buildExportUrl(
                'bpa/findings/export-csv/',
                findingsDataQueryParams as Record<string, unknown>,
                selectedRowIds,
              )
            : ''
        }
        className="float-right"
        urlParams={{}}
        onSuccessCallback={() =>
          showSuccessToast('Success! File download completed.')
        }
        onFailureCallback={(message) => showFailureToast('Error! ' + message)}
      />
      {isAssigneeModalOpen && (
        <BPAAssigneeModal
          isOpen={isAssigneeModalOpen}
          toggle={() => setAssigneeModalOpen(false)}
          isBulk={true}
        />
      )}
      {isWatcherBulkModalOpen && (
        <BPAWatcherModal
          onClose={() => setWatcherBulkModalOpen(false)}
          findingIds={selectedRowIds}
          isBulk={true}
        />
      )}
      {isMarkAllAsReadConfirmationModalOpen && (
        <MarkAllAsReadConfirmationModal />
      )}
    </>
  );
};

export default BulkActions;
